import { AccountTypeIdEnum } from '../models/transactions/enum/account-type-id.enum';
import { PaymentInstructionOrigin } from '../features/manual-payments/interfaces/payment-instruction-origin';

export class Routes {
  // Global
  static profile(): string {
    return `/profile/view`;
  }

  static accounts(): string {
    return `/accounts`;
  }

  static signIn(): string {
    return `/auth/sign-in`;
  }

  // Internal
  static ipMemberAccount(memberId: number, accountId: number) {
    return `/records/members/${memberId}/accounts/${accountId}`;
  }

  static ipMemberAccounts(memberId: number) {
    return `/records/members/${memberId}`;
  }

  static ipCreateRemittance() {
    return `/remittances/create`;
  }

  static ipProcessRemittance(remittanceId: number) {
    return `/remittances/${remittanceId}/process`;
  }

  static ipEditRemittance(remittanceId: number) {
    return `/remittances/${remittanceId}/edit`;
  }

  static ipEditScheduledRemittance(remittanceId: number) {
    return `/remittances/schedules/${remittanceId}/edit`;
  }

  static ipFundAllocations(memberId: number, accountType: number) {
    return `/records/members/${memberId}/accounts/allocation/${accountType}`;
  }

  static ipInvestmentInstructions(memberId: number, accountType: number) {
    return `/records/members/${memberId}/accounts/instructions/${accountType}`;
  }

  static ipMemberRecord(memberId: number): string {
    return `/records/members/${memberId}`;
  }

  static ipTransferInRequest(memberId: number): string {
    return `/records/members/${memberId}/transfer-in-request`;
  }

  static ipUpdateAccountTypeBeneficiaries(memberId: number, accountTypeId: number): string {
    return `/records/members/${memberId}/accounts/${accountTypeId}/beneficiaries`;
  }

  static ipMemberVariableBenefitConfiguration(memberId: number): string {
    return `/records/members/${memberId}/variable-benefit-configuration`;
  }

  static ipMemberUpdateVariableBenefitConfiguration(memberId: number): string {
    return `/records/members/${memberId}/update-variable-benefit-configuration`;
  }

  static ipMemberCreateAnnuityAccountConfiguration(memberId: number): string {
    return `/records/members/${memberId}/annuity-account-configuration`;
  }

  static ipSplitPension(memberId: number, accountId: number, instructionId: string): string {
    return `/records/members/${memberId}/accounts/${accountId}/payment-instructions/${instructionId}/split`;
  }

  static ipAllMembers(): string {
    return `/records/members`;
  }

  static ipViewMemberSubmissionDetails(submissionId: number): string {
    return `/submissions/member/${submissionId}`;
  }

  static ipViewEmployerSubmissionDetails(submissionId: number): string {
    return `/submissions/employer/${submissionId}`;
  }

  static ipViewSubmissions(): string {
    return `/submissions`;
  }

  static ipCreateFiduciary(memberId: number): string {
    return `/records/members/${memberId}/fiduciaries/create`;
  }

  static ipEditFiduciary(memberId: number): string {
    return `/records/members/${memberId}/fiduciaries/edit`;
  }

  static ipAddMember(): string {
    return `/records/add-member`;
  }

  static ipAddMultipleMembers(): string {
    return `/records/add-multiple-members`;
  }

  static ipAddNonMember(): string {
    return `/records/add-non-member`;
  }

  static ipCreateVbConfiguration(memberId: number): string {
    return `/records/members/${memberId}/variable-benefit-configuration`;
  }

  static ipViewAllEmployers(): string {
    return `/records/employers`;
  }

  static ipViewEmployerRecord(employerId: number): string {
    return `/records/employers/${employerId}`;
  }

  static ipUpdateEmployerContacts(employerId: number): string {
    return `/records/employers/${employerId}/update-contacts`;
  }

  static ipViewEmployerUserRecord(employerId: number, userId: number): string {
    return `/records/employers/${employerId}/users/${userId}`;
  }

  static ipViewAllRemittances(): string {
    return `/remittances/queue`;
  }

  static ipChangeVariableBenefitPayments(memberId: number): string {
    return `/records/members/${memberId}/change-variable-benefit-payments`;
  }

  static ipManualPayments(): string {
    return `/accounting/manual-payments`;
  }

  static ipNewManualPaymentInstruction(origin: PaymentInstructionOrigin): string {
    return `/accounting/manual-payments/new/${origin.type}/${origin.id}`;
  }

  static ipManualPaymentInstruction(instructionId: number): string {
    return `/accounting/manual-payments/${instructionId}`;
  }

  static ipInboundPayments(): string {
    return `/accounting/inbound-payments`;
  }

  static ipInboundBatch(batchId: number): string {
    return `/accounting/inbound-payments/${batchId}`;
  }

  static ipOutboundPayments(): string {
    return `/accounting/outbound-payments`;
  }

  static ipPaymentBatch(batchId: number): string {
    return `/accounting/outbound-payments/${batchId}`;
  }

  static ipConsentWaivers(memberId: number): string {
    return `/records/members/${memberId}/consent-waivers`;
  }

  // Employer

  static epCreateRemittance(employerId: string) {
    return `/remittances/create/${employerId}`;
  }

  static epAllEmployees(): string {
    return `/records/employees`;
  }

  static epEmployeeRecord(employeeId: number): string {
    return `/records/employees/${employeeId}`;
  }

  static epAddSingleEmployee(): string {
    return `/records/employees/add-employee`;
  }

  static epAddMultipleEmployees(): string {
    return `/records/employees/add-employees`;
  }

  static epRemittances(employerId: string): string {
    return `/remittances/employers/${employerId}/remittances`;
  }

  static epSelfEnrolment(): string {
    return `/auth/employer-self-enrolment`;
  }

  static epEmployerUsers(): string {
    return `/settings/employer-users`;
  }

  static epEmployerUserDetails(userId: number): string {
    return `/settings/employer-users/${userId}`;
  }

  static epEmployerRecord(): string {
    return `/settings/employer-record`;
  }

  static epUpdateEmployerContacts(): string {
    return `/settings/employer-record/update-contacts`;
  }

  static epEmployerSubmissionDetails(submissionId: number): string {
    return `/settings/employer-record/submissions/${submissionId}`;
  }

  // Member
  static mpAllSubmissions() {
    return `/profile/member-submissions`;
  }

  static mpSubmissionDetails(submissionId: number) {
    return `/profile/member-submissions/${submissionId}`;
  }

  static mpUpdateAccountTypeBeneficiaries(accountTypeId: number): string {
    return `/accounts/${accountTypeId}/beneficiaries`;
  }

  static mpAccounts(): string {
    return `/accounts`;
  }

  static mpSetInvestmentInstructions(accountTypeId: number): string {
    return `/accounts/instructions/${accountTypeId}`;
  }

  static mpSelfEnrolment(): string {
    return `/auth/member-self-enrolment`;
  }

  static mpEnterEmailVerification(): string {
    return `/auth/member-enter-email-verification`;
  }

  static mpAccountDetails(accountTypeId: AccountTypeIdEnum): string {
    return `/accounts/${accountTypeId}/details`;
  }

  static mpAnnuityAccountDetails(accountId: number): string {
    return `/accounts/annuity/${accountId}/details`;
  }

  static mpChangeVariableBenefitPayments(): string {
    return `/accounts/change-variable-benefit-payments`;
  }

  static mpTransferInRequest(): string {
    return `/accounts/transfer-in-request`;
  }

  // External
  static sppContact(): string {
    return `https://www.saskpension.com/contact/`;
  }
}
